import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { tenantService } from "../../services/tenantService";
import { provisioningService } from "../../services/provisioningService";
import { Tenant } from "../../types";

export default function TenantList() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<string | null>(null);

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantService.list();
      setTenants(res.data.data.tenants);
    } catch (e) {
      console.error(e);
      setError("Errore nel caricamento dei tenant");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleDeactivate = async (tenantId: string) => {
    if (!window.confirm("Disattivare questo tenant? L'operazione è irreversibile.")) return;
    setDeactivating(tenantId);
    try {
      await provisioningService.deactivateTenant(tenantId);
      await fetchTenants();
    } catch (e) {
      console.error(e);
      setError("Errore durante la disattivazione del tenant");
    } finally {
      setDeactivating(null);
    }
  };

  return (
    <div className="container">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <FormLabel label="Lista Tenant" cols={2}/>
        <Button
          style={{ flex: "1 1 20%"}}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/chatbot/tenants/new")}
        >
          Nuovo tenant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Phone Number ID</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Creato il</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nessun tenant trovato
                  </TableCell>
                </TableRow>
              )}
              {tenants.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.wa_phone_number_id}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.active ? "Attivo" : "Inattivo"}
                      color={t.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(t.created_at).toLocaleDateString("it-IT")}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Dettaglio">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/chatbot/tenants/${t.id}`)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {t.active && (
                      <Tooltip title="Disattiva tenant">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deactivating === t.id}
                          onClick={() => handleDeactivate(t.id)}
                        >
                          {deactivating === t.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
